import React, { useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { ArrowUpRightFromSquare } from '@/atoms/Icons/ArrowUpRightFromSquare'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { Footer, HeadingLG, LabelMD, ParagraphLG } from '@/atoms/Text'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { InvestmentBannerResult } from '@/services/CmsService/InvestmentBanner'
import { isHasTypeName } from '@/services/RenderService'
import { useUserRegion } from '@/services/UserRegionService'
import { isGuildFullySupported } from '@/utils/GuildUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { ResponsiveBackgroundImage } from '../../atoms/ResponsiveBackgroundImage'

export const isInvestmentBanner = (content: unknown) =>
  isHasTypeName(content) && content.__typename === 'InvestmentBanner'

interface Props extends InvestmentBannerResult {
  className?: string
  hideTrailer?: boolean
}

export const InvestmentBanner: React.FC<Props> = ({
  className,
  hideTrailer,
  ctaText,
  description,
  desktopBackgroundImageCloudinaryPath,
  gravity,
  interstitialButtonText,
  interstitialText,
  logoCloudinaryPath,
  mobileBackgroundImageCloudinaryPath,
  offeringUrl,
  tabletBackgroundImageCloudinaryPath,
  title,
  trailerYouTubeId,
  subsidiaryName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { t } = useTranslate('common')
  const { region } = useUserRegion()
  const shouldRender = isGuildFullySupported(region)
  const leavingMessage =
    interstitialText || t('youAreLeavingToRegA', 'You are now leaving Angel Studios, Inc. to view a Reg A+ offering')
  const leavingContinueLabel = interstitialButtonText || t('continueToOffering', 'Continue to Offering')

  const cloudinaryImgPaths = {
    mobile: String(mobileBackgroundImageCloudinaryPath),
    tablet: String(tabletBackgroundImageCloudinaryPath),
    desktop: String(desktopBackgroundImageCloudinaryPath),
  }

  if (!shouldRender || !offeringUrl) return null

  return (
    <BeforeYouLeaveProvider>
      <BeforeYouLeaveModal />
      <div className={classNames('flex flex-col gap-6', className)}>
        <div className="relative" id="investment-banner">
          <ResponsiveBackgroundImage
            src={cloudinaryImgPaths.desktop}
            srcUrls={{
              xs: cloudinaryImgPaths.mobile,
              sm: cloudinaryImgPaths.tablet,
              md: cloudinaryImgPaths.tablet,
              lg: cloudinaryImgPaths.desktop,
              xl: cloudinaryImgPaths.desktop,
            }}
            gravity={gravity || 'g_face'}
            responsiveAR={{
              xs: '9:16',
              sm: 1,
              md: '16:9',
              lg: '16:9',
              xl: '16:9',
              '2xl': '16:9',
              '3xl': '32:9',
            }}
            backgroundPosition="bg-top"
          />
          <div
            className={classNames(
              'relative flex flex-col items-center gap-4 bg-gradient-to-t from-black via-black to-black/0 px-10 pb-6 pt-12 mt-[400px] text-center',
              'md:items-start md:px-12 md:pb-12 md:text-left lg:pb-14 lg:bg-gradient-to-r lg:mr-[50%] lg:mt-0 lg:pt-40 2xl:pt-56 3xl:pt-64 sm:pl-8 md:pl-12 xl:pl-16',
            )}
          >
            <div className="flex flex-col items-center gap-3 md:max-w-[520px] md:items-start">
              {logoCloudinaryPath && <Image src={logoCloudinaryPath} alt={title || ''} width={200} height={97} />}
              <HeadingLG color="white" weight="bold" className="md:photon-display md:max-w-[430px]">
                {title}
              </HeadingLG>
              <ParagraphLG color="white" className="text-pretty">
                {description}
              </ParagraphLG>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <AngelFundingLink
                href={offeringUrl}
                linkContext="Investment Banner"
                leavingMessage={leavingMessage}
                leavingContinueLabel={leavingContinueLabel}
                className="w-auto"
              >
                <Button size="md" variant="white">
                  <LabelMD className="flex flex-row gap-2 text-nowrap">
                    {ctaText}
                    <ArrowUpRightFromSquare color="black" size={16} />
                  </LabelMD>
                </Button>
              </AngelFundingLink>
              {!hideTrailer && trailerYouTubeId && (
                <Button
                  variant="plain"
                  onClick={() => setIsModalOpen(true)}
                  className="flex shrink-0 items-center justify-center gap-2 px-2 py-3 text-white"
                >
                  <PlayIcon size={24} /> {t('watchTrailer', 'Watch Trailer')}
                </Button>
              )}
            </div>
          </div>
        </div>
        <Footer color="core-gray-400" weight="medium" className="mx-4 text-pretty sm:mx-8 md:mx-12 xl:mx-16">
          <Translate
            i18nKey="launchMovieOfferingDisclaimerWithSubsidiaryName"
            t={t}
            values={{ offeringUrl, subsidiaryName }}
          >
            Disclaimer: {{ subsidiaryName }}, a subsidiary of Angel Studios, Inc. is conducting an offering of
            securities under Regulation A. Investing in this offering is high-risk and neither {{ subsidiaryName }}, nor
            its parent company Angel Studios guarantee performance of the investment. You should not invest unless you
            can afford to lose the full amount invested. A description of the terms of the offering and your investment
            (including the timing, manner and amount of any return), can be found in the subscription agreement
            available at{' '}
            <AngelFundingLink
              href={offeringUrl}
              linkContext="Investment Banner"
              className="inline-block !w-fit cursor-pointer underline"
              leavingMessage={leavingMessage}
              leavingContinueLabel={leavingContinueLabel}
            >
              {offeringUrl}
            </AngelFundingLink>
            . You should not invest until you have reviewed and understood the offering documents, including the risk
            factors contained in its offering circular.
          </Translate>
        </Footer>
      </div>
      {trailerYouTubeId && (
        <YoutubeVideoModal
          youTubeUrl={`https://www.youtube-nocookie.com/embed/${trailerYouTubeId}`}
          handleClose={() => setIsModalOpen(false)}
          showModal={isModalOpen}
        />
      )}
    </BeforeYouLeaveProvider>
  )
}
